import {
  insertableCaseDetailTables,
  insertableFuneralHomeDetails,
  insertableTemplates,
} from 'utils'
import { t } from 'i18next'
import { insertableCaseDetails } from 'utils/tinyMCE/insertMenu/insertableCaseDetails'
import { insertableHeaders } from 'utils/tinyMCE/insertMenu/insertableHeaders'

export const insertDetailsMenu = ({ editor }) => {
  editor.ui.registry.addMenuButton('dynamicText', {
    text: t('editor.tinyMCE.insertDetail.text'),
    fetch: (callback) => callback(populateInsertMenu({ editor })),
  })
}

export default insertDetailsMenu

const populateInsertMenu = (editorProps) => {
  const { editor } = editorProps
  const currentData = editor.customData || {}
  const propsForUtils = { editor, ...currentData }

  const templates = insertableTemplates(propsForUtils)
  const caseDetails = insertableCaseDetails(propsForUtils) || []
  const caseDetailTables = insertableCaseDetailTables(propsForUtils) || []
  const funeralHomeDetails = insertableFuneralHomeDetails(propsForUtils) || []
  const headers = insertableHeaders(propsForUtils) || []

  return sortInsertMenuItems({
    templates,
    caseDetails,
    caseDetailTables,
    funeralHomeDetails,
    headers,
  })
}

const sortInsertMenuItems = ({
  templates: passedTemplates,
  caseDetails,
  caseDetailTables,
  funeralHomeDetails,
  headers,
}) => {
  const templates = [...passedTemplates]

  const nameOfDeceased = caseDetails.find(
    (item) => item.text === 'Name Of Deceased',
  )

  const nameComponentTexts = [
    'Prefix Name',
    'First Name',
    'Nickname',
    'Middle Name',
    'Last Name',
    'Suffix Name',
    'Maiden Name',
  ]

  const otherItems = caseDetails.filter(
    (item) =>
      item !== nameOfDeceased && !nameComponentTexts.includes(item.text),
  )

  const nameComponents = caseDetails.filter((item) =>
    nameComponentTexts.includes(item.text),
  )

  // --- Specific Reordering Logic for 'Final Resting' from caseDetails ---
  const finalRestingFromDetailsIndex = otherItems.findIndex(
    (item) => item.text === 'Final Resting',
  )

  if (finalRestingFromDetailsIndex !== -1) {
    let firstIntermentIndexForDetails = otherItems.findIndex((item) =>
      item.text?.startsWith('Interment '),
    )
    if (firstIntermentIndexForDetails !== -1) {
      const [finalRestingItem] = otherItems.splice(
        finalRestingFromDetailsIndex,
        1,
      )
      // Recalculate index after splice
      firstIntermentIndexForDetails = otherItems.findIndex((item) =>
        item.text?.startsWith('Interment '),
      )
      otherItems.splice(firstIntermentIndexForDetails, 0, finalRestingItem)
    }
  }
  // --- End Specific Reordering Logic ---

  // Find and remove 'Final Resting' from templates array
  const finalRestingTemplateIndex = templates.findIndex(
    (item) => item.text === 'Final Resting',
  )
  let finalRestingItemFromTemplate = null
  if (finalRestingTemplateIndex !== -1) {
    ;[finalRestingItemFromTemplate] = templates.splice(
      finalRestingTemplateIndex,
      1,
    )
  }

  // Insert 'Final Resting' (from templates) into otherItems before the first Interment field
  if (finalRestingItemFromTemplate) {
    const firstIntermentIndexForTemplateItem = otherItems.findIndex((item) =>
      item.text?.startsWith('Interment '),
    )
    if (firstIntermentIndexForTemplateItem !== -1) {
      otherItems.splice(
        firstIntermentIndexForTemplateItem,
        0,
        finalRestingItemFromTemplate,
      )
    } else {
      // If no Interment field, append at the end of otherItems
      otherItems.push(finalRestingItemFromTemplate)
    }
  }

  // Move 'Genealogy' (from templates) directly below the deceased's parents
  const genealogyTemplateIndex = templates.findIndex(
    (item) => item.text === 'Genealogy',
  )
  if (genealogyTemplateIndex !== -1) {
    const [genealogyItem] = templates.splice(genealogyTemplateIndex, 1)
    const parentTexts = ['Mother Of Deceased', 'Father Of Deceased']
    const lastParentIndex = otherItems.reduce(
      (last, item, index) => (parentTexts.includes(item.text) ? index : last),
      -1,
    )
    if (lastParentIndex !== -1) {
      otherItems.splice(lastParentIndex + 1, 0, genealogyItem)
    } else {
      // Parents not present on this case → restore Genealogy to its original
      // position in the templates list
      templates.splice(genealogyTemplateIndex, 0, genealogyItem)
    }
  }

  // Combine in the final order
  const finalItems = [
    nameOfDeceased,
    ...otherItems,
    ...caseDetailTables,
    ...funeralHomeDetails,
    ...templates, // This is the modified 'templates' copy
    ...nameComponents,
    ...headers,
  ].filter(Boolean) // Filter out any potentially undefined items

  // Map and translate the text property
  const translatedItems = finalItems.map((item) => ({
    ...item,
    text: translateText(item.text),
  }))
  return translatedItems
}

// Helper to translate text with fallback
const translateText = (text) => {
  const textWithoutSpaces = text.toLowerCase().trim().replace(/\s+/g, '')
  return (
    t(`editor.tinyMCE.insertDetail.${textWithoutSpaces}`, {
      defaultValue: text,
    }) || text
  )
}
